import { CheckCircleIcon, ArrowPathIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import ProgressBar from '../core/ProgressBar';
import { isEmpty } from '@/utils/miscUtils';
import { useEffect, useRef, useState } from 'react';
import usePrevious from '@/hooks/usePrevious';
import {
  useAnalysisErrors,
  useAnalysisTypes,
  useIsAnalysisInProgress,
  useIsAnalysisStale,
  useIsLCAAndTEAStatus,
} from '@/stores/analysisStatusStore';
import { ANALYSIS_STATUS, ANALYSIS_TYPES } from '@/consts';
import { analysisStatusActions } from '@/stores/analysisStatusStore';

const styles = {
  succeeded: 'bg-green-75',
  running: 'bg-blue-75',
  pending: 'bg-blue-75',
  failed: 'bg-red-75',
  cancelled: 'bg-red-75',
};

const iconStyles = {
  succeeded: 'stroke-green-400',
  running: 'stroke-blue-400',
  pending: 'stroke-blue-400',
  failed: 'stroke-red-400',
  cancelled: 'stroke-red-400',
};

const icons = {
  succeeded: CheckCircleIcon,
  running: ArrowPathIcon,
  pending: ArrowPathIcon,
  failed: XCircleIcon,
  cancelled: XCircleIcon,
};

const messages = {
  succeeded: label => `${label} Analysis Complete`,
  pending: label => `${label} Analysis in Pending`,
  running: label => `Processing ${label} Analysis`,
  failed: label => `${label} Analysis Unsuccessful`,
  cancelled: label => `${label} Analysis Cancelled`,
};

export const StatusAlert = ({ status, label, progress, errors }) => {
  const [isVisible, setIsVisible] = useState(true);
  const previousStatus = usePrevious(status);
  const progressStartRef = useRef(null);

  useEffect(() => {
    if (status === ANALYSIS_STATUS.running && progress > 0 && progress < 100) {
      if (progressStartRef.current === null) {
        progressStartRef.current = { time: Date.now(), progress };
      }
    } else {
      progressStartRef.current = null;
    }
  }, [status, progress]);

  const getRemainingLabel = () => {
    if (!progressStartRef.current || status !== ANALYSIS_STATUS.running) return null;
    const progressDone = progress - progressStartRef.current.progress;
    if (progressDone <= 0) return null;
    const elapsedSec = (Date.now() - progressStartRef.current.time) / 1000;
    const remaining = Math.round((elapsedSec / progressDone) * (100 - progress));
    if (remaining < 60) return `~${remaining}s remaining`;
    return `~${Math.ceil(remaining / 60)}m remaining`;
  };

  useEffect(() => {
    if (status === ANALYSIS_STATUS.running) {
      setIsVisible(true);
      return;
    }

    if (status === ANALYSIS_STATUS.succeeded) {
      if (!previousStatus) {
        return setIsVisible(false);
      }

      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [status, previousStatus]);

  if (!isVisible) return null;

  const Icon = icons[status];
  const className = styles[status];
  const iconStyle = iconStyles[status];
  const message = messages?.[status]?.(label);

  // Add rotation animation for running and pending states
  const iconClasses = `h-5 w-5 flex-shrink-0 stroke-2 ${iconStyle}`;
  const animatedIconClasses =
    status === 'running' || status === 'pending' ? `${iconClasses} animate-spin-slow` : iconClasses;

  return (
    <div className={`w-full rounded p-3 mt-5 ${className}`}>
      <div className="flex items-start gap-2">
        <Icon className={animatedIconClasses} />
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
          {ANALYSIS_STATUS.failed === status && (
            <div className="text-xs">{errors || 'Our support team has been alerted and is looking into the issue'}</div>
          )}
          {progress >= 0 && status === ANALYSIS_STATUS.running && (
            <ProgressBar value={progress} rightLabel={getRemainingLabel()} />
          )}
        </div>
        {(status === ANALYSIS_STATUS.running || status === ANALYSIS_STATUS.pending) && (
          <button onClick={analysisStatusActions.cancelAnalysis}>
            <XMarkIcon className="h-4 w-4 flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
};

StatusAlert.propTypes = {
  status: PropTypes.oneOf(['succeeded', 'running', 'pending', 'failed', 'cancelled']).isRequired,
  label: PropTypes.string.isRequired,
  progress: PropTypes.number,
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const StatusAlerts = () => {
  const types = useAnalysisTypes();
  const isAnalysisInProgress = useIsAnalysisInProgress();
  const isAnalysisStale = useIsAnalysisStale();
  const lcaAndTeaStatus = useIsLCAAndTEAStatus();
  const errors = useAnalysisErrors();

  const getTeaAndLcaErrors = () => {
    const teaError = errors?.['tea'];
    const lcaError = errors?.['lca'];

    if (teaError && lcaError) {
      return (
        <>
          <p className="mb-2">TEA: {teaError}</p>
          <p>LCA: {lcaError}</p>
        </>
      );
    }

    if (teaError) {
      return teaError;
    }

    if (lcaError) {
      return lcaError;
    }

    return null;
  };

  if ((isAnalysisStale && !isAnalysisInProgress) || isEmpty(types)) {
    return null;
  }

  const otherAnalysisTypes = Object.entries(types).filter(([type]) => !['tea', 'lca'].includes(type));

  return (
    <div className="absolute flex flex-col top-14 right-0 mr-2 w-[310px]">
      <StatusAlert status={lcaAndTeaStatus} label="TEA + LCA" errors={getTeaAndLcaErrors()} />
      {otherAnalysisTypes.map(
        ([type, { status, progress }]) =>
          status && (
            <StatusAlert
              key={type}
              status={status}
              label={ANALYSIS_TYPES[type]}
              errors={errors?.[type]}
              progress={(progress ?? 0) * 100}
            />
          ),
      )}
    </div>
  );
};

export default StatusAlerts;
